import { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Show } from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { ComposerForm } from "./ComposerForm";
import { ComposerFooterRow } from "./ComposerFooterRow";
import { EmailRoutingSelect } from "./EmailRoutingSelect";
import { SmsRoutingSelect } from "./SmsRoutingSelect";
import {
  recipientKey,
  type Recipient,
  type MultiRecipientControls
} from "./multi-recipient";
import { SendEmailTemplateDialog } from "./SendEmailTemplateDialog";
import { ReplyBanner } from "./ReplyBanner";
import { VoiceComposer } from "./VoiceComposer";
import { CallNumberFields } from "./CallNumberFields";
import { useCallSelection } from "./useCallSelection";
import { useSoftphone } from "softphone";
import { type ComposerTab, type ComposerFormChannel } from "./constants";
import { useCrmContact } from "../api/crm";
import { useConversation, useSetAiSuggestions } from "../api/hooks";
import { useComposerTabs } from "../hooks/useComposerTabs";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import { useInboxContext } from "../context/useInboxContext";
import { useAuthContext, useVoiceEnabled } from "providers/Auth";
import type { EmailTemplate } from "../../settings-communication/api/types";
import type { ReplyTarget } from "../types";

interface MessageComposerProps {
  conversationId?: string;
  contactId?: string;
  draftContactId?: number;
  /** Selected channel. Owned by the host: `?tab=` on /inbox, chip state in the pop-up. */
  activeTab: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  onMessageSent?: () => void;
  replyTarget?: ReplyTarget | null;
  onCancelReply?: () => void;
  /** Hides the channel tab row when the host renders its own (the pop-up's chips). */
  hideTabs?: boolean;
  // Reports the currently selected SMS "from" number up so sibling components
  // (e.g. the suggestion card) can send from the same number.
  onSmsSelectionChange?: (phoneNumberId: string, toPhone: string) => void;
}

// The mode annotation appended to the "AI replies enabled" switch label.
// Module-level so it isn't rebuilt on every render.
const AI_MODE_SUFFIX: Record<string, string> = {
  auto_response: " (auto-response)",
  suggest: " (suggest)"
};

/* eslint-disable sonarjs/cognitive-complexity -- unified composer host: email + sms + voice + chat + AI suggestions */
export const MessageComposer = ({
  conversationId,
  contactId,
  draftContactId,
  activeTab,
  onTabChange,
  onMessageSent,
  replyTarget,
  onCancelReply,
  hideTabs = false,
  onSmsSelectionChange
}: MessageComposerProps) => {
  const { type } = useAuthContext();
  const isVoiceEnabled = useVoiceEnabled();
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);

  const {
    visibleTabs,
    chatTabAvailable,
    hasEligibleApplications,
    canonicalApplicationId,
    inviteModal
  } = useComposerTabs(contactId);

  const [selectedMailbox, setSelectedMailbox] = useState<string>("");
  const [selectedToEmail, setSelectedToEmail] = useState<string>("");
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>("");
  const [selectedToPhone, setSelectedToPhone] = useState<string>("");
  const [isToPhoneOptedOut, setIsToPhoneOptedOut] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [extraRecipients, setExtraRecipients] = useState<Recipient[]>([]);
  const [primaryExcluded, setPrimaryExcluded] = useState(false);

  const addRecipient = useCallback((recipient: Recipient) => {
    setExtraRecipients((prev) =>
      prev.some(
        (existing) =>
          recipientKey(existing.contactId, existing.address) ===
          recipientKey(recipient.contactId, recipient.address)
      )
        ? prev
        : [...prev, recipient]
    );
  }, []);

  const removeRecipient = useCallback((recipient: Recipient) => {
    setExtraRecipients((prev) => {
      const next = prev.filter(
        (existing) =>
          recipientKey(existing.contactId, existing.address) !==
          recipientKey(recipient.contactId, recipient.address)
      );
      if (next.length === 0) setPrimaryExcluded(false);
      return next;
    });
  }, []);

  const multiRecipientControls = useMemo<MultiRecipientControls>(
    () => ({
      extraRecipients,
      onAddRecipient: addRecipient,
      onRemoveRecipient: removeRecipient,
      primaryExcluded,
      onPrimaryExcludedChange: setPrimaryExcluded
    }),
    [extraRecipients, addRecipient, removeRecipient, primaryExcluded]
  );

  const handleTemplateSelect = useCallback((template: EmailTemplate) => {
    setSelectedTemplateId(template.id);
  }, []);

  const handleTemplateDialogClose = useCallback(() => {
    setSelectedTemplateId(null);
  }, []);
  const { data: contact } = useCrmContact(contactId || "");
  const { data: conversationData } = useConversation(conversationId || "");
  const setAiSuggestions = useSetAiSuggestions(conversationId || "");
  const aiSuggestionsEnabled =
    conversationData?.conversation?.ai_suggestions_enabled ?? true;
  // Only false when the backend positively reports the conversation's number
  // is not enrolled with an active AI leasing agent.
  const aiSuggestionsAvailable =
    conversationData?.conversation?.ai_suggestions_available !== false;
  // Label the switch with its on/off state plus the leasing agent's current
  // mode when known: auto-response actually sends replies, suggest only drafts.
  const aiRepliesOn = aiSuggestionsEnabled && aiSuggestionsAvailable;
  // Only annotate the mode when replies are actually on — "AI replies disabled
  // (auto-response)" is self-contradictory. (The backend also returns
  // message_mode: null unless the auto-response kill-switch is on, so an
  // unauthorized partner never sees the auto-response label.)
  const aiSwitchLabel = `AI replies ${aiRepliesOn ? "enabled" : "disabled"}${
    aiRepliesOn
      ? (AI_MODE_SUFFIX[conversationData?.conversation?.message_mode ?? ""] ??
        "")
      : ""
  }`;
  const {
    state: { selectedSubject },
    actions: { clearTargetMessage }
  } = useInboxContext();

  // The chat is sent about the subject open in the Details panel: the contact's
  // applicant on that application (so the SMS mirrors to THIS contact, not the
  // application's primary applicant), falling back to their most-recent app.
  const activeApplicationId =
    selectedSubject?.type === "application"
      ? Number(selectedSubject.id)
      : canonicalApplicationId;
  const recipientApplicantId = useMemo(
    () =>
      contact?.applicants?.find(
        (applicant) => applicant.application_id === activeApplicationId
      )?.id,
    [contact?.applicants, activeApplicationId]
  );

  useEffect(() => {
    setExtraRecipients([]);
    setPrimaryExcluded(false);
  }, [conversationId, contactId, activeTab]);

  const handleMessageSent = useCallback(() => {
    setExtraRecipients([]);
    setPrimaryExcluded(false);
    onMessageSent?.();
  }, [onMessageSent]);

  const handleTabChange = useCallback(
    (tab: ComposerTab) => {
      clearTargetMessage();
      onTabChange(tab);
    },
    [onTabChange, clearTargetMessage]
  );

  const handleEmailSelectionChange = useCallback(
    (mailboxId: string, toEmail: string) => {
      setSelectedMailbox(mailboxId);
      setSelectedToEmail(toEmail);
    },
    []
  );

  const handleSmsSelectionChange = useCallback(
    (phoneNumberId: string, toPhone: string, isToOptedOut: boolean) => {
      setSelectedPhoneId(phoneNumberId);
      setSelectedToPhone(toPhone);
      setIsToPhoneOptedOut(isToOptedOut);
      onSmsSelectionChange?.(phoneNumberId, toPhone);
    },
    [onSmsSelectionChange]
  );

  const contactName = useMemo(() => {
    if (!contact) return "";
    // Filter nulls so template literal doesn't coerce them to "null".
    return [contact.first_name, contact.last_name]
      .filter((part): part is string => !!part && part.trim().length > 0)
      .join(" ")
      .trim();
  }, [contact]);

  const isReply = !!replyTarget;
  const chatUsable = chatTabAvailable && hasEligibleApplications;
  const requestedTab: ComposerTab = isReply ? "email" : activeTab;
  // Resolve to a tab that's actually usable: email when available, else the
  // first visible tab (e.g. Chat for native-chat-only partners with no email).
  // A LOCKED email or Call tab (no communication_email / communication_phone)
  // is visible only for the upsell and is skipped here like a hidden one, so a
  // reply or a stale `?tab=` can't land the composer on a channel the partner
  // cannot send from — nor on the "Make a call" form under a locked Call tab.
  const isLocked = (tab: (typeof visibleTabs)[number]) =>
    (tab.value === "email" || tab.value === "voice") && tab.disabled;
  const emailLocked = visibleTabs.some(
    (tab) => tab.value === "email" && tab.disabled
  );
  const voiceLocked = visibleTabs.some(
    (tab) => tab.value === "voice" && tab.disabled
  );
  const emailUsable =
    visibleTabs.some((tab) => tab.value === "email") && !emailLocked;
  const fallbackTab: ComposerTab | undefined = emailUsable
    ? "email"
    : visibleTabs.find((tab) => !isLocked(tab))?.value;
  const requestedUsable =
    visibleTabs.some((tab) => tab.value === requestedTab) &&
    !(requestedTab === "email" && emailLocked) &&
    !(requestedTab === "voice" && (voiceLocked || !isVoiceEnabled)) &&
    !(requestedTab === "chat" && !chatUsable);
  const effectiveTab: ComposerTab | undefined = requestedUsable
    ? requestedTab
    : fallbackTab;

  const { activeCall } = useSoftphone();
  const callSelection = useCallSelection(
    contactId || "",
    effectiveTab === "voice" && !activeCall
  );

  // The footer holds the channel tabs, the AI-suggestions switch and the
  // per-channel routing selects. The pop-up renders its own chips and hides the
  // tabs (CRM-1133), which can leave the row with nothing in it — skip it then
  // instead of drawing an empty 80px strip.
  // Not in the pop-up: the design's footer there is just the input card and the
  // SMS note, and a second AI toggle a few centimetres from the Settings
  // popover's Application-support-agent one reads as a duplicate of it. The
  // suggestions themselves still render in the thread; the switch lives in
  // /inbox, which is also the only place it appears consistently (it needs a
  // conversation, so a fresh draft never had it).
  const showAiSuggestionsSwitch =
    !!conversationId && type.isCrmAiSuggestedReplies && !isCompact;
  // In the pop-up the From/To selects render in the participants block under
  // the chips (portaled below), not in the composer footer.
  const showEmailRouting = effectiveTab === "email" && !isCompact;
  const showSmsRouting = effectiveTab === "sms" && !isCompact;
  const showCallNumberFields =
    effectiveTab === "voice" &&
    !activeCall &&
    callSelection.toOptions.length > 0;
  const hasComposerFooter =
    (!isReply && (!hideTabs || showAiSuggestionsSwitch)) ||
    showEmailRouting ||
    showSmsRouting ||
    showCallNumberFields;

  if (!conversationId && draftContactId === undefined) return null;

  // No channel is available to this partner. The fallback used to default to
  // "email", which mounted the email composer for partners without the email
  // entitlement (CRM-1132) — render no composer instead.
  if (!effectiveTab) return null;

  return (
    <div
      className={cx(
        "bg-white border-t border-alpha-100",
        // The design's pop-up footer insets the input card; /inbox's composer
        // spans the pane and pads itself (12px top, 8px to the SMS note).
        isCompact ? "px-5 pt-3 pb-2" : ""
      )}
    >
      {isReply && onCancelReply ? (
        <ReplyBanner
          target={replyTarget}
          onCancel={onCancelReply}
          compact={isCompact}
        />
      ) : null}

      {/* Pop-up only: the routing selects live in the header's participants
          block. Portaling (rather than moving the components) keeps their
          selection state and default-picking effects mounted even while the
          block is collapsed, so sends always have a From/To. */}
      {isCompact && embed?.participantsEl && effectiveTab === "email"
        ? createPortal(
            <EmailRoutingSelect
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleEmailSelectionChange}
              menuPlacement="auto"
              compact
            />,
            embed.participantsEl
          )
        : null}
      {isCompact && embed?.participantsEl && effectiveTab === "sms"
        ? createPortal(
            <SmsRoutingSelect
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleSmsSelectionChange}
              menuPlacement="auto"
              compact
            />,
            embed.participantsEl
          )
        : null}

      <Show if={hasComposerFooter}>
        <ComposerFooterRow
          tabs={visibleTabs}
          active={effectiveTab}
          onTabChange={handleTabChange}
          showTabs={!isReply && !hideTabs}
          leftExtra={
            !isReply && showAiSuggestionsSwitch ? (
              <Tooltip>
                {/* Disabled elements don't fire hover events — the wrapping
                    div keeps the tooltip working while the switch is off. */}
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      size={SwitchSize.SM}
                      checked={aiSuggestionsEnabled && aiSuggestionsAvailable}
                      onChange={(value) => setAiSuggestions.mutate(value)}
                      disabled={
                        setAiSuggestions.isPending || !aiSuggestionsAvailable
                      }
                      label={aiSwitchLabel}
                    />
                  </div>
                </TooltipTrigger>
                {!aiSuggestionsAvailable && (
                  <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
                    The recipient phone number doesn&apos;t have an active AI
                    leasing agent.
                  </TooltipContent>
                )}
              </Tooltip>
            ) : undefined
          }
        >
          {showEmailRouting ? (
            <EmailRoutingSelect
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleEmailSelectionChange}
              multiRecipient={isReply ? undefined : multiRecipientControls}
            />
          ) : null}

          {showSmsRouting ? (
            <SmsRoutingSelect
              contactId={contactId}
              conversationId={conversationId}
              onSelectionChange={handleSmsSelectionChange}
              multiRecipient={isReply ? undefined : multiRecipientControls}
            />
          ) : null}

          {showCallNumberFields ? (
            <CallNumberFields
              selection={callSelection}
              menuPlacement="top"
              inline
            />
          ) : null}
        </ComposerFooterRow>
      </Show>

      {effectiveTab === "voice" ? (
        <VoiceComposer contactId={contactId} selection={callSelection} />
      ) : (
        <ComposerForm
          channel={effectiveTab as ComposerFormChannel}
          conversationId={conversationId}
          draftContactId={draftContactId}
          mailboxId={selectedMailbox}
          toEmail={selectedToEmail}
          toName={contactName}
          phoneNumberId={selectedPhoneId}
          toPhone={selectedToPhone}
          applicationId={activeApplicationId}
          recipientApplicantId={recipientApplicantId}
          isToOptedOut={isToPhoneOptedOut}
          contactId={contactId ? Number(contactId) : undefined}
          replyToMessageId={replyTarget?.messageId}
          extraRecipients={extraRecipients}
          primaryExcluded={primaryExcluded}
          onMessageSent={handleMessageSent}
          onTemplateSelect={handleTemplateSelect}
        />
      )}

      {emailUsable &&
      selectedTemplateId !== null &&
      (conversationId || draftContactId !== undefined) ? (
        <SendEmailTemplateDialog
          key={selectedTemplateId}
          open
          onClose={handleTemplateDialogClose}
          templateId={selectedTemplateId}
          conversationId={conversationId}
          draftContactId={draftContactId}
          contactId={contactId}
          replyToMessageId={replyTarget?.messageId}
          onMessageSent={onMessageSent}
        />
      ) : null}

      {inviteModal}
    </div>
  );
};
